import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Bot } from "lucide-react";
import api from "@/lib/api";
import { CHATBOT } from "@/constants/testIds";

const SUGGEST = [
  "What is WeAutomate's OEM/ODM offering?",
  "Show me your smart home product line",
  "How does the franchise program work?",
  "I want a quote for industrial automation",
];

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi — I'm WeAutomate's assistant. Ask me about OEM/ODM, smart-home, industrial automation, or how to partner with us." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }]);
    setBusy(true);
    try {
      const { data } = await api.post("/chatbot", { session_id: sessionId, message });
      if (!sessionId) setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — I couldn't reach the server. Please try again or use the contact form." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid={CHATBOT.toggle} onClick={() => setOpen(!open)}
              className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full gradient-bg shadow-[0_10px_40px_rgba(157,76,221,0.5)] flex items-center justify-center hover:scale-105 transition-transform">
        {open ? <X className="w-5 h-5" /> : <MessageSquare className="w-5 h-5" />}
      </button>
      {open && (
        <div data-testid={CHATBOT.panel}
             className="fixed bottom-24 right-6 z-50 w-[min(380px,calc(100vw-3rem))] h-[560px] max-h-[80vh] glass rounded-2xl flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
              <Bot className="w-4 h-4" />
            </div>
            <div>
              <div className="text-sm font-semibold">WeAutomate Assistant</div>
              <div className="text-[10px] text-white/40 font-mono uppercase tracking-widest">Powered by AI</div>
            </div>
          </div>
          <div data-testid={CHATBOT.messages} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scrollbar-hide">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  m.role === "user" ? "bg-accent text-white" : "bg-white/5 text-white/90"
                }`}>{m.content}</div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white/5 px-3.5 py-2.5 rounded-2xl text-sm">
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 bg-white/40 rounded-full animate-pulse" />
                    <span className="w-1.5 h-1.5 bg-white/40 rounded-full animate-pulse [animation-delay:0.15s]" />
                    <span className="w-1.5 h-1.5 bg-white/40 rounded-full animate-pulse [animation-delay:0.3s]" />
                  </span>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          {messages.length <= 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {SUGGEST.map((s) => (
                <button key={s} onClick={() => send(s)}
                        className="text-[11px] px-2.5 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/70">
                  {s}
                </button>
              ))}
            </div>
          )}
          <div className="p-3 border-t border-white/10 flex gap-2">
            <input
              data-testid={CHATBOT.input}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask anything…"
              className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm focus:border-[#9D4CDD] outline-none"
            />
            <button data-testid={CHATBOT.send} onClick={() => send()} disabled={busy}
                    className="w-10 h-10 rounded-full bg-accent flex items-center justify-center disabled:opacity-50">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
